import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Brain, Send, Plus, AlertTriangle, CheckCircle2, Clock, Sparkles, User, ShieldAlert, CalendarDays, Stethoscope, Search } from 'lucide-react';
import { toast } from 'sonner';

const URGENCY_STYLES = {
  emergency: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: AlertTriangle },
  urgent: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: AlertTriangle },
  routine: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: CheckCircle2 },
  follow_up: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: Clock },
};

export default function TriagePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [newPatientName, setNewPatientName] = useState('');
  const [newSymptoms, setNewSymptoms] = useState('');
  const [recommendedDoctors, setRecommendedDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [bookingDate, setBookingDate] = useState(null);
  const [bookingNotes, setBookingNotes] = useState('');
  const [settings, setSettings] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const fetchSessions = async (q) => {
    try {
      let url = '/triage/sessions?limit=50';
      if (q) url += `&search=${encodeURIComponent(q)}`;
      const res = await API.get(url);
      setSessions(res.data.sessions || []);
    } catch (err) { console.error(err); }
  };

  const loadSession = async (id) => {
    try {
      const res = await API.get(`/triage/sessions/${id}`);
      setActiveSession(res.data);
      if (res.data.status === 'completed' && res.data.id) {
        fetchRecommendedDoctors(res.data.id);
      }
    } catch (err) { console.error(err); }
  };

  const fetchRecommendedDoctors = async (sessionId) => {
    try {
      const res = await API.get(`/doctors/recommend/${sessionId}`);
      setRecommendedDoctors(res.data.recommended_doctors || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    fetchSessions(searchQuery);
    API.get('/settings').then(r => setSettings(r.data)).catch(() => {});
    const sid = searchParams.get('session');
    if (sid) loadSession(sid);
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeSession?.messages]);

  useEffect(() => {
    if (!sending && activeSession?.status === 'in_progress') {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [sending, activeSession?.messages?.length]);

  const startSession = async () => {
    try {
      const res = await API.post('/triage/sessions', {
        patient_name: newPatientName || 'Walk-in Patient',
        initial_symptoms: newSymptoms || undefined
      });
      setActiveSession(res.data);
      setNewDialogOpen(false);
      setNewPatientName('');
      setNewSymptoms('');
      fetchSessions(searchQuery);
      toast.success('Triage session started');
    } catch (err) { toast.error('Failed to start session'); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !activeSession || sending) return;
    setSending(true);
    const currentMsg = message;
    setMessage('');

    setActiveSession(prev => ({
      ...prev,
      messages: [...prev.messages, { role: 'user', content: currentMsg, timestamp: new Date().toISOString() }]
    }));

    try {
      const res = await API.post(`/triage/sessions/${activeSession.id}/messages`, { message: currentMsg });
      setActiveSession(res.data);
      fetchSessions(searchQuery);
      if (res.data.status === 'completed') {
        toast.success('Triage assessment complete');
        fetchRecommendedDoctors(res.data.id);
      }
    } catch (err) {
      toast.error('Failed to send message');
      setActiveSession(prev => ({ ...prev, messages: prev.messages.slice(0, -1) }));
      setMessage(currentMsg);
    } finally { setSending(false); }
  };

  const escalateSession = async () => {
    if (!activeSession) return;
    try {
      const res = await API.post(`/triage/sessions/${activeSession.id}/escalate`);
      setActiveSession(res.data);
      fetchSessions(searchQuery);
      toast.success('Session escalated for human review');
    } catch (err) { toast.error('Failed to escalate'); }
  };

  const bookAppointment = async () => {
    if (!selectedDoctor) { toast.error('Please select a doctor'); return; }
    try {
      const doctor = recommendedDoctors.find(d => d.id === selectedDoctor);
      await API.post('/appointments', {
        patient_name: activeSession?.patient_name || 'Patient',
        doctor_id: selectedDoctor,
        doctor_name: doctor?.name || '',
        date: bookingDate ? bookingDate.toISOString().split('T')[0] : '',
        triage_session_id: activeSession?.id,
        notes: bookingNotes
      });
      toast.success('Appointment booked successfully!');
      setBookingOpen(false);
      setSelectedDoctor('');
      setBookingDate(null);
      setBookingNotes('');
    } catch (err) { toast.error('Failed to book appointment'); }
  };

  const assessment = activeSession?.triage_summary;
  const urgencyStyle = assessment ? URGENCY_STYLES[assessment.urgency_level] : null;
  const UrgencyIcon = urgencyStyle?.icon || AlertTriangle;
  const showSummary = settings?.show_triage_summary_to_patient !== false;

  const cleanContent = (text) => {
    if (!text) return '';
    return text.replace(/```json[\s\S]*?```/g, '').trim();
  };

  return (
    <div className="flex h-full" data-testid="triage-page">
      <div className="w-72 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-100 space-y-3">
          <Dialog open={newDialogOpen} onOpenChange={setNewDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-triage-btn" className="w-full bg-sky-600 hover:bg-sky-700">
                <Plus className="w-4 h-4 mr-2" /> New Triage
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle style={{fontFamily: 'Manrope'}}>Start New Triage Session</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label>Patient Name</Label>
                  <Input data-testid="new-triage-patient-name" value={newPatientName} onChange={e => setNewPatientName(e.target.value)} placeholder="Patient name (optional)" />
                </div>
                <div className="space-y-2">
                  <Label>Initial Symptoms (optional)</Label>
                  <Input data-testid="new-triage-symptoms" value={newSymptoms} onChange={e => setNewSymptoms(e.target.value)} placeholder="e.g., headache and fever for 2 days" />
                </div>
                <Button data-testid="start-triage-submit" onClick={startSession} className="w-full bg-sky-600 hover:bg-sky-700">
                  <Brain className="w-4 h-4 mr-2" /> Start Triage
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              data-testid="triage-search"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); fetchSessions(e.target.value); }}
              className="pl-9 h-9 text-sm"
            />
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1" data-testid="session-list">
            {sessions.map((s) => (
              <button key={s.id} data-testid={`session-item-${s.id}`} onClick={() => loadSession(s.id)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${activeSession?.id === s.id ? 'bg-sky-50 border border-sky-200' : 'hover:bg-slate-50'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-900 truncate">{s.patient_name || 'Walk-in'}</span>
                  {s.urgency_level && <span className={`triage-badge triage-${s.urgency_level} !text-[10px] !px-1.5 !py-0.5`}>{s.urgency_level.replace('_', ' ')}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-[10px]">{s.status}</Badge>
                  <span className="text-[10px] text-slate-400">{new Date(s.created_at).toLocaleDateString()}</span>
                </div>
              </button>
            ))}
            {sessions.length === 0 && (
              <div className="text-center py-8 text-slate-400 text-sm"><Brain className="w-6 h-6 mx-auto mb-2 opacity-50" /><p>No sessions yet</p></div>
            )}
          </div>
        </ScrollArea>
      </div>

      <div className="flex-1 flex flex-col bg-white">
        {activeSession ? (
          <>
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>{activeSession.patient_name || 'Walk-in Patient'}</h2>
                <p className="text-xs text-slate-500">Session started {new Date(activeSession.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2">
                {activeSession.status === 'in_progress' && (
                  <Button data-testid="escalate-btn" variant="outline" size="sm" onClick={escalateSession} className="text-amber-600 border-amber-200 hover:bg-amber-50">
                    <ShieldAlert className="w-4 h-4 mr-1" /> Escalate
                  </Button>
                )}
                {activeSession.status === 'completed' && recommendedDoctors.length > 0 && (
                  <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="book-appointment-btn" size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                        <CalendarDays className="w-4 h-4 mr-1" /> Book Appointment
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader><DialogTitle style={{fontFamily: 'Manrope'}}>Book Appointment</DialogTitle></DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Select Doctor</Label>
                          <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
                            <SelectTrigger data-testid="booking-doctor-select"><SelectValue placeholder="Choose a doctor" /></SelectTrigger>
                            <SelectContent>
                              {recommendedDoctors.map(d => (
                                <SelectItem key={d.id} value={d.id}>
                                  <span className="font-medium">{d.name}</span> - <span className="text-slate-500">{d.specialty}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Preferred Date</Label>
                          <Calendar mode="single" selected={bookingDate} onSelect={setBookingDate} className="rounded-md border" disabled={(date) => date < new Date()} />
                        </div>
                        <div className="space-y-2">
                          <Label>Notes</Label>
                          <Input data-testid="booking-notes" value={bookingNotes} onChange={e => setBookingNotes(e.target.value)} placeholder="Any additional notes..." />
                        </div>
                        <Button data-testid="confirm-booking-btn" onClick={bookAppointment} className="w-full bg-sky-600 hover:bg-sky-700">
                          Confirm Booking
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
                <Badge variant={activeSession.status === 'completed' ? 'default' : 'secondary'} className={activeSession.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : activeSession.status === 'escalated' ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'}>
                  {activeSession.status}
                </Badge>
              </div>
            </div>

            {assessment && showSummary && (
              <div className={`mx-6 mt-4 p-4 rounded-xl border ${urgencyStyle?.bg} ${urgencyStyle?.border}`} data-testid="triage-assessment">
                <div className="flex items-start gap-3">
                  <UrgencyIcon className={`w-5 h-5 mt-0.5 ${urgencyStyle?.text}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-sm font-bold uppercase ${urgencyStyle?.text}`}>{assessment.urgency_level?.replace('_', ' ')}</span>
                      {assessment.confidence_score && <span className="text-xs text-slate-500">Confidence: {Math.round(assessment.confidence_score * 100)}%</span>}
                    </div>
                    <p className="text-sm text-slate-700 mb-1">{assessment.summary || assessment.recommended_action}</p>
                    {assessment.recommended_doctor && (
                      <p className="text-sm font-medium text-sky-700 flex items-center gap-1.5 mt-1">
                        <Stethoscope className="w-3.5 h-3.5" /> Recommended: {assessment.recommended_doctor}
                      </p>
                    )}
                    {assessment.primary_symptoms?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {assessment.primary_symptoms.map((s, i) => <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>)}
                      </div>
                    )}
                    {assessment.needs_human_review && <p className="text-xs text-amber-600 mt-2 font-medium">Human review recommended</p>}
                  </div>
                </div>
              </div>
            )}

            {activeSession.status === 'completed' && recommendedDoctors.length > 0 && (
              <div className="mx-6 mt-3 p-3 rounded-lg bg-sky-50 border border-sky-200" data-testid="doctor-recommendations">
                <p className="text-xs font-semibold text-sky-800 mb-2 flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5" /> Recommended Doctors</p>
                <div className="flex flex-wrap gap-2">
                  {recommendedDoctors.map(d => (
                    <button key={d.id} data-testid={`doctor-rec-${d.id}`} onClick={() => { setSelectedDoctor(d.id); setBookingOpen(true); }}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-sky-200 hover:border-sky-400 transition-colors text-left">
                      <div className="w-7 h-7 rounded-full bg-sky-100 flex items-center justify-center">
                        <Stethoscope className="w-3.5 h-3.5 text-sky-600" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-900">{d.name}</p>
                        <p className="text-[10px] text-slate-500">{d.specialty}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <ScrollArea className="flex-1 px-6 py-4">
              <div className="space-y-4 max-w-3xl mx-auto" data-testid="chat-messages">
                {activeSession.messages?.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                    <div className="flex items-start gap-2 max-w-[80%]">
                      {msg.role === 'assistant' && (
                        <div className="w-7 h-7 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-1">
                          <Sparkles className="w-3.5 h-3.5 text-sky-600" />
                        </div>
                      )}
                      <div className={`chat-message ${msg.role === 'user' ? 'chat-message-user' : 'chat-message-assistant'}`}>
                        {msg.role === 'assistant' ? (
                          <div className="prose prose-sm max-w-none prose-p:my-1 prose-strong:text-slate-900 prose-li:my-0.5 prose-ul:my-1 prose-ol:my-1">
                            <ReactMarkdown
                              components={{ p: ({children}) => <p className="my-1">{children}</p>, a: ({href, children}) => <a href={href} className="text-sky-600 underline">{children}</a> }}>
                              {cleanContent(msg.content)}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                        )}
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-1">
                          <User className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start animate-fade-in">
                    <div className="flex items-start gap-2">
                      <div className="w-7 h-7 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-1"><Sparkles className="w-3.5 h-3.5 text-sky-600" /></div>
                      <div className="chat-message chat-message-assistant">
                        <div className="flex gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{animationDelay: '0ms'}} />
                          <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{animationDelay: '150ms'}} />
                          <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{animationDelay: '300ms'}} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {assessment && showSummary && (
                  <div className="animate-fade-in max-w-3xl mx-auto space-y-3" data-testid="inline-summary">
                    <div className={`p-4 rounded-xl border-2 ${urgencyStyle?.bg} ${urgencyStyle?.border}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <UrgencyIcon className={`w-5 h-5 ${urgencyStyle?.text}`} />
                        <span className={`text-sm font-bold uppercase ${urgencyStyle?.text}`}>{assessment.urgency_level?.replace('_', ' ')}</span>
                        {assessment.confidence_score && <span className="text-xs text-slate-500 ml-auto">Confidence: {Math.round(assessment.confidence_score * 100)}%</span>}
                      </div>
                      <p className="text-sm text-slate-700 font-medium mb-1">Summary</p>
                      <p className="text-sm text-slate-600">{assessment.summary || assessment.recommended_action}</p>
                      {assessment.primary_symptoms?.length > 0 && (
                        <div className="mt-2"><p className="text-xs text-slate-500 mb-1">Symptoms identified:</p><div className="flex flex-wrap gap-1">{assessment.primary_symptoms.map((s, i) => <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>)}</div></div>
                      )}
                      {assessment.recommended_doctor && <p className="text-sm text-sky-700 font-medium mt-2 flex items-center gap-1.5"><Stethoscope className="w-4 h-4" /> {assessment.recommended_doctor}</p>}
                      {assessment.needs_human_review && <p className="text-xs text-amber-600 mt-2 font-medium">Human review recommended</p>}
                      <p className="text-[10px] text-slate-400 mt-2 italic">This is an AI-assisted triage assessment, not a medical diagnosis. Please consult a healthcare professional.</p>
                    </div>

                    {(activeSession.recommended_doctors || recommendedDoctors).length > 0 && (
                      <div className="p-4 rounded-xl bg-sky-50 border border-sky-200">
                        <p className="text-sm font-semibold text-sky-800 mb-3 flex items-center gap-1.5"><Stethoscope className="w-4 h-4" /> Book with a Doctor</p>
                        <div className="space-y-2">
                          {(activeSession.recommended_doctors || recommendedDoctors).map(d => (
                            <button key={d.id} data-testid={`inline-doctor-${d.id}`} onClick={() => { setSelectedDoctor(d.id); setBookingOpen(true); }}
                              className="w-full flex items-center gap-3 p-3 bg-white rounded-lg border border-sky-100 hover:border-sky-300 hover:shadow-sm transition-all text-left">
                              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                                <Stethoscope className="w-5 h-5 text-sky-600" />
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-slate-900">{d.name}</p>
                                <p className="text-xs text-slate-500">{d.specialty}</p>
                              </div>
                              <div className="text-xs text-sky-600 font-medium flex items-center gap-1">
                                <CalendarDays className="w-3.5 h-3.5" /> Book
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>
            </ScrollArea>

            <div className="px-6 py-4 border-t border-slate-100">
              <form onSubmit={sendMessage} className="flex gap-3 max-w-3xl mx-auto">
                <Input ref={inputRef} data-testid="triage-message-input" value={message} onChange={e => setMessage(e.target.value)}
                  placeholder={activeSession.status === 'completed' ? "Ask a follow-up question..." : "Describe your symptoms..."} className="flex-1 h-11" disabled={sending} autoFocus />
                <Button data-testid="triage-send-btn" type="submit" disabled={!message.trim() || sending} className="h-11 px-6 bg-sky-600 hover:bg-sky-700">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
              <p className="text-[11px] text-slate-400 text-center mt-2 max-w-3xl mx-auto">
                AI-assisted triage is not a substitute for professional medical evaluation. If you are experiencing a medical emergency, call emergency services immediately.
              </p>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 rounded-2xl bg-sky-50 flex items-center justify-center mx-auto mb-4"><Brain className="w-8 h-8 text-sky-600" /></div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>AI Triage Engine</h3>
              <p className="text-sm text-slate-500 mb-6">Start a new triage session or select an existing one. Our AI assistant will assess symptoms and recommend the appropriate level of care.</p>
              <Button data-testid="start-triage-empty" onClick={() => setNewDialogOpen(true)} className="bg-sky-600 hover:bg-sky-700"><Plus className="w-4 h-4 mr-2" /> New Triage Session</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
